

import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Avatar } from '../../fieldsComponents/appointments/avatar';
import imageCompression from 'browser-image-compression';
import { useNotifications } from './notifications'; // Import useNotifications hook
import LoadingMask from './loadingMask'; // Import the LoadingMask component

function ProfileDialog({ isOpen, onClose, acctNo, acctId, userId, userEmail, acctName, userName, phone, profileImageUrl, onUpdateProfileImage, onUpdateUserEmail, onUpdateUserName, onUpdatePhone }) {
  // Add a ref to track and preserve active tab
  const activeTabRef = React.useRef('profile');
  const [activeTab, setActiveTab] = useState('profile');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newEmail, setNewEmail] = useState(userEmail);
  const [newUserName, setNewUserName] = useState(userName || userEmail?.split('@')[0] || 'User');
  const [newPhoneNumber, setNewPhoneNumber] = useState(phone || '');
  const [profileImage, setProfileImage] = useState(profileImageUrl);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false); // State for loading mask
  const [isEditingName, setIsEditingName] = useState(false); // State to track if name is being edited

  const { showSuccess, showError, NotificationComponent } = useNotifications(); // Use notifications
  // Google Link Tab state
  const [isGoogleConnected, setIsGoogleConnected] = useState(null);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showGoogleEmailInput, setShowGoogleEmailInput] = useState(false);
  const [googleEmail, setGoogleEmail] = useState('');

  // Check Google connection status when Google Link Tab is opened
  useEffect(() => {
    if (activeTab === 'google-link' && userId) {
      setIsGoogleLoading(true);
      const token = localStorage.getItem('token');
      fetch('/api/ui/appointment/gCal/getGoogleCheck', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ userId })
      })
        .then(res => res.json())
        .then(data => {
          setIsGoogleConnected(!!data.googleEmailExists);
          setGoogleEmail(data.google_email || '');
          // If Google email is connected and google_picture is present, use it as profile image
          if ((data.tokenExists || data.googleEmailExists) && data.google_picture) {
            setProfileImage(data.google_picture);
          }
        })
        .catch(() => {
          setIsGoogleConnected(false);
          setGoogleEmail('');
        })
        .finally(() => setIsGoogleLoading(false));
    }
  }, [activeTab, userId]);

  useEffect(() => {
    setNewEmail(userEmail);
  }, [userEmail]);

  // Update newUserName when userName prop changes
  useEffect(() => {
    if (userName) {
      setNewUserName(userName);
    } else if (userEmail) {
      setNewUserName(userEmail.split('@')[0]);
    }
  }, [userName, userEmail]);

  // Update newPhoneNumber when phone prop changes
  useEffect(() => {
    if (phone) {
      setNewPhoneNumber(phone);
    }
  }, [phone]);

  // Add effect to update local state when profile image URL from props changes
  useEffect(() => {
    if (profileImageUrl) {
      setProfileImage(profileImageUrl);
    }
  }, [profileImageUrl]);

  // Add effect to preserve tab selection
  useEffect(() => {
    if (isOpen) {
      // If dialog is opened, update the ref with current tab selection
      activeTabRef.current = activeTab;
    }
  }, [isOpen, activeTab]);

  // Add effect to restore tab when props change but dialog remains open
  useEffect(() => {
    if (isOpen && activeTabRef.current !== activeTab) {
      // This handles the case where props change but we want to keep the selected tab
      setActiveTab(activeTabRef.current);
    }
  }, [isOpen, profileImageUrl, userEmail, userName, phone]);

  // Custom tab setter to update both state and ref
  const setActiveTabWithRef = async (tab) => {
    setActiveTab(tab);
    activeTabRef.current = tab;
    // If switching to the phone tab, fetch the latest phone number from backend
    if (tab === 'phone' && userId) {
      try {
        const token = localStorage.getItem('token');
        const getPhoneResponse = await fetch(`/api/ui/appointment/gCal/getPhoneNumber/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (getPhoneResponse.ok) {
          const getPhoneData = await getPhoneResponse.json();
          if (getPhoneData && getPhoneData.phone) {
            setNewPhoneNumber(getPhoneData.phone);
          }
        }
      } catch (error) {
        // Optionally handle error (e.g., show notification)
        // showError('Failed to fetch phone number from backend.');
      }
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        showError("Only JPEG or PNG files are allowed!");
        return;
      }

      // Display the uploaded image immediately
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setProfileImage(fileReader.result); // Set the image preview
      };
      fileReader.readAsDataURL(file);

      try {
        setIsUploading(true); // Show loading mask
        let compressedFile = file;
        if (file.size > 400 * 400) {
          const fileOptions = {
            maxSizeMB: 0.25, // Max file size (in MB)
            maxWidthOrHeight: 1200, // Max width or height to maintain quality
            useWebWorker: true, // Enable multi-threading for performance
            maxIteration: 3, // Increase iterations to improve quality
            initialQuality: 0.9, // Start with high quality
          };
          const compressedObject = await imageCompression(file, fileOptions);
          compressedFile = new File([compressedObject], file.name, { type: file.type });
        }
        // Upload the file
        const formData = new FormData();
        formData.append('image', compressedFile);
        formData.append('userId', userId); // Use userId instead of acctId

        const token = localStorage.getItem('token');
        const response = await fetch(`/api/ui/appointment/gCal/myprofileUpload/${userId}`, { // Use userId in URL instead of acctId
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData,
        });

        const result = await response.json();

        if (response.ok && result.success) {
          // Get the correct image URL from the response
          let imageUrl = result.data?.imageUrl || result.fileUrl;

          if (imageUrl) {
            // Create a stable cache-busting parameter using a single timestamp
            const timestamp = Date.now();
            const cacheBustUrl = imageUrl.includes('?') ? imageUrl : `${imageUrl}?t=${timestamp}`;

            // Store clean URL in localStorage without the cache-busting parameter
            const originalUrl = imageUrl.split('?')[0];
            localStorage.setItem('profileImageUrl', originalUrl);

            // Preload the image before setting it in state
            const img = new Image();
            img.onload = () => {
              // After image is loaded and cached by browser, update the state
              setProfileImage(cacheBustUrl);
            };
            img.src = cacheBustUrl;

            // Update parent component with the original URL (no cache busting)
            if (typeof onUpdateProfileImage === 'function') {
              onUpdateProfileImage(originalUrl);
            }

            // Set profile image immediately to show feedback
            setProfileImage(cacheBustUrl);

            showSuccess('Image uploaded successfully!');
          } else {
            showError("Failed to get uploaded image URL");
          }
        } else {
          throw new Error(result.message || result.error || "Failed to upload image");
        }
      } catch (error) {
        console.error("Upload error:", error);
        showError("Failed to upload image: " + (error.message || "Unknown error"));
      } finally {
        setIsUploading(false); // Hide loading mask
      }
    }
  };
  const handleChangePassword = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Stop event bubbling

    // Remember current tab
    const currentTab = activeTabRef.current;

    // Validation
    if (newPassword !== confirmPassword) {
      showError('New passwords do not match');
      return;
    }

    // Get userId from localStorage if not provided in props
    const userIdToUse = userId || localStorage.getItem('userId');

    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/ui/appointment/gCal/changePassword', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ acctId, newPassword, userId: userIdToUse, password: newPassword }),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();
      showSuccess('Password changed successfully');

      // Reset form fields
      setNewPassword('');
      setConfirmPassword('');
      setIsSubmitting(false);

      // Restore the tab we were on
      setActiveTabWithRef(currentTab);
    } catch (error) {
      showError('Failed to change password. Please try again.');
      setIsSubmitting(false);
    }
  };
  const handleUpdateUserName = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    // Validation
    if (!newUserName || newUserName.trim() === '') {
      showError('Username cannot be empty');
      setNewUserName(userName || userEmail?.split('@')[0] || 'User');
      setIsEditingName(false);
      return;
    }

    // Check if required parameters are available
    if (!userId) {
      showError('Cannot update username: Missing user identification');
      setIsEditingName(false);
      return;
    }

    // If the name hasn't changed, just exit edit mode
    if (newUserName === userName) {
      setIsEditingName(false);
      return;
    }

    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/ui/appointment/gCal/updateUserName', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId,
          name: newUserName
        }),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();

      // Update localStorage with new username
      localStorage.setItem('userName', newUserName);

      // Call the callback first (React state updates)
      if (typeof onUpdateUserName === 'function') {
        onUpdateUserName(newUserName);
      }

      // Then dispatch the event for other components that might be listening
      const userUpdatedEvent = new CustomEvent('userNameUpdated', {
        detail: { userName: newUserName }
      });
      window.dispatchEvent(userUpdatedEvent);

      showSuccess('Username updated successfully');
      setIsEditingName(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error updating username:', error);
      showError('Failed to update username. Please try again.');
      setIsEditingName(false);
      setIsSubmitting(false);
    }
  };
  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Stop event bubbling

    // Remember current tab
    const currentTab = activeTabRef.current;

    // Validation
    if (!newEmail) {
      showError('Email is required');
      return;
    }

    // Check if required parameters are available
    if (!userId && !acctId) {
      showError('Cannot update email: Missing user identification');
      return;
    }

    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/ui/appointment/gCal/updateEmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId,  // Send userId (will be undefined/null if not available)
          newEmail
        }),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();

      // Update the email in the dashboard
      if (typeof onUpdateUserEmail === 'function') {
        onUpdateUserEmail(newEmail);
      }

      showSuccess('Email updated successfully');
      setIsSubmitting(false);

      // Restore the tab we were on
      setActiveTabWithRef(currentTab);
    } catch (error) {
      showError('Failed to update email. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleUpdatePhoneNumber = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Stop event bubbling

    // Remember current tab
    const currentTab = activeTabRef.current;

    // Validation
    if (!newPhoneNumber) {
      showError('Phone number is required');
      return;
    }

    // Basic phone number validation
    const phoneRegex = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
    if (!phoneRegex.test(newPhoneNumber)) {
      showError('Please enter a valid phone number');
      return;
    }

    // Check if required parameters are available
    if (!userId) {
      showError('Cannot update phone number: Missing user identification');
      return;
    }

    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/ui/appointment/gCal/updatePhoneNumber', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId,
          phone: newPhoneNumber // Use 'phone' instead of 'phoneNumber' to match backend
        }),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      // After updating, fetch the latest phone number from backend
      const getPhoneResponse = await fetch(`/api/ui/appointment/gCal/getPhoneNumber/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!getPhoneResponse.ok) throw new Error(`Failed to fetch phone number from backend. Status: ${getPhoneResponse.status}`);
      const getPhoneData = await getPhoneResponse.json();
      const latestPhone = getPhoneData && getPhoneData.phone ? getPhoneData.phone : newPhoneNumber;

      // Update local state and localStorage with the latest phone number
      setNewPhoneNumber(latestPhone);
      localStorage.setItem('phone', latestPhone);

      // Call the callback first (React state updates)
      if (typeof onUpdatePhone === 'function') {
        onUpdatePhone(latestPhone);
      }

      // Then dispatch the event for other components that might be listening
      const phoneUpdatedEvent = new CustomEvent('phoneUpdated', {
        detail: { phone: latestPhone }
      });
      window.dispatchEvent(phoneUpdatedEvent);

      showSuccess('Phone number updated successfully');
      setIsSubmitting(false);

      // Restore the tab we were on
      setActiveTabWithRef(currentTab);
    } catch (error) {
      console.error('Error updating phone number:', error);
      showError('Failed to update phone number. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <NotificationComponent /> {/* Add NotificationComponent */}

      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
          <div className="relative">
            <button
              type="button"
              onClick={onClose}
              className="absolute top-1 right-1 p-1 rounded-full bg-black hover:bg-gray-800 transition-colors duration-200 text-white hover:text-gray-200 z-10"
              aria-label="Close"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Header with border-bottom */}
          <div className="border-b border-gray-200 py-4 mb-8">
            <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
              My Profile
            </Dialog.Title>
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 py-2 mb-8 overflow-x-auto">
            <button
              className={`py-2 px-4 font-medium text-sm ${activeTab === 'profile'
                ? 'border-b-2 border-black text-black'
                : 'text-gray-500 hover:text-gray-700'
                }`}
              onClick={() => setActiveTabWithRef('profile')}
            >
              Profile
            </button>
            <button
              className={`py-2 px-4 font-medium text-sm ${activeTab === 'password'
                ? 'border-b-2 border-black text-black'
                : 'text-gray-500 hover:text-gray-700'
                }`}
              onClick={() => setActiveTabWithRef('password')}
            >
              Change Password
            </button>
            <button
              className={`py-2 px-4 font-medium text-sm ${activeTab === 'email'
                ? 'border-b-2 border-black text-black'
                : 'text-gray-500 hover:text-gray-700'
                }`}
              onClick={() => setActiveTabWithRef('email')}
            >
              Update Email
            </button>
            <button
              className={`py-2 px-4 font-medium text-sm ${activeTab === 'phone'
                ? 'border-b-2 border-black text-black'
                : 'text-gray-500 hover:text-gray-700'
                }`}
              onClick={() => setActiveTabWithRef('phone')}
            >
              Update Phone
            </button>
            <button
              className={`py-2 px-4 font-medium text-sm ${activeTab === 'google-link'
                ? 'border-b-2 border-black text-black'
                : 'text-gray-500 hover:text-gray-700'
                }`}
              onClick={() => setActiveTabWithRef('google-link')}
            >
              Google Link
            </button>
          </div>
          {/* Google Link Tab */}
          {activeTab === 'google-link' && (
            <div className="mt-8">

              {isGoogleLoading ? (
                <p className="text-gray-600">Checking Google account status...</p>
              ) : isGoogleConnected ? (
                <div className="flex flex-col sm:flex-row gap-2 items-center justify-center">
                  {googleEmail && (
                    <span className="flex items-center gap-2 ml-2 text-gray-700">
                      <svg width="20" height="20" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_17_40)">
                          <path d="M47.532 24.552c0-1.636-.146-3.2-.418-4.704H24.48v9.02h13.02c-.56 3.02-2.24 5.58-4.78 7.3v6.06h7.72c4.52-4.16 7.09-10.28 7.09-17.676z" fill="#4285F4" />
                          <path d="M24.48 48c6.48 0 11.92-2.14 15.89-5.82l-7.72-6.06c-2.14 1.44-4.88 2.3-8.17 2.3-6.28 0-11.6-4.24-13.5-9.96H2.67v6.24C6.62 43.98 14.88 48 24.48 48z" fill="#34A853" />
                          <path d="M10.98 28.46c-.5-1.44-.78-2.98-.78-4.46s.28-3.02.78-4.46v-6.24H2.67A23.97 23.97 0 000 24c0 3.98.96 7.76 2.67 11.18l8.31-6.72z" fill="#FBBC05" />
                          <path d="M24.48 9.54c3.53 0 6.68 1.22 9.17 3.62l6.87-6.87C36.39 2.14 30.96 0 24.48 0 14.88 0 6.62 4.02 2.67 12.82l8.31 6.24c1.9-5.72 7.22-9.96 13.5-9.96z" fill="#EA4335" />
                        </g>
                        <defs>
                          <clipPath id="clip0_17_40">
                            <rect width="48" height="48" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      {googleEmail}
                    </span>
                  )}

                  <button
                    className="ml-2 p-1 rounded hover:bg-gray-200 text-red-600 transition-colors duration-150"
                    title="Remove connected Google email"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const userId = localStorage.getItem('userId');
                        const res = await fetch('/api/ui/appointment/gCal/removeGoogleEmail', {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({ userId })
                        });
                        const data = await res.json();
                        if (res.ok && data.success) {
                          showSuccess('Google email removed successfully!');
                          setIsGoogleConnected(false);
                          setGoogleEmail('');
                        } else {
                          showError(data.message || 'Failed to remove Google email.');
                        }
                      } catch (err) {
                        showError('Failed to remove Google email.');
                      }
                    }}
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              ) : showGoogleEmailInput ? (
                <div className="flex flex-col sm:flex-row gap-2 items-center">
                  <input
                    type="email"
                    className="border border-gray-300 rounded px-2 py-1"
                    placeholder="Enter your Google email"
                    value={googleEmail}
                    onChange={e => setGoogleEmail(e.target.value)}
                    autoFocus
                  />
                </div>
              ) : (
                <div className="flex justify-center">
                  <button
                    className="flex items-center justify-center gap-2 px-4 py-2 rounded bg-black text-white font-semibold w-full max-w-xs shadow-sm hover:bg-gray-900 border border-black"
                    onClick={async () => {
                      try {
                        const userId = localStorage.getItem('userId');
                        const res = await fetch('/api/user/google', {
                          method: 'POST',
                          credentials: 'include',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ userId })
                        });
                        if (res.ok) {
                          const data = await res.json();
                          if (data && data.url) {
                            // Append userId as a query param for redirect
                            const url = new URL(data.url);
                            if (userId) url.searchParams.set('userId', userId);
                            window.location.href = url.toString();
                          } else {
                            // fallback with userId param
                            window.location.href = `http://localhost:8083/api/user/login/google/oAuth2/callback?userId=${encodeURIComponent(userId)}`;
                          }
                        } else {
                          window.location.href = `http://localhost:8083/api/user/login/google/oAuth2/callback?userId=${encodeURIComponent(userId)}`;
                        }
                      } catch {
                        const userId = (typeof window !== 'undefined' && window.localStorage.getItem('userId')) || '';
                        window.location.href = `http://localhost:8083/api/user/login/google/oAuth2/callback?userId=${encodeURIComponent(userId)}`;
                      }
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_17_40)">
                        <path d="M47.532 24.552c0-1.636-.146-3.2-.418-4.704H24.48v9.02h13.02c-.56 3.02-2.24 5.58-4.78 7.3v6.06h7.72c4.52-4.16 7.09-10.28 7.09-17.676z" fill="#4285F4" />
                        <path d="M24.48 48c6.48 0 11.92-2.14 15.89-5.82l-7.72-6.06c-2.14 1.44-4.88 2.3-8.17 2.3-6.28 0-11.6-4.24-13.5-9.96H2.67v6.24C6.62 43.98 14.88 48 24.48 48z" fill="#34A853" />
                        <path d="M10.98 28.46c-.5-1.44-.78-2.98-.78-4.46s.28-3.02.78-4.46v-6.24H2.67A23.97 23.97 0 000 24c0 3.98.96 7.76 2.67 11.18l8.31-6.72z" fill="#FBBC05" />
                        <path d="M24.48 9.54c3.53 0 6.68 1.22 9.17 3.62l6.87-6.87C36.39 2.14 30.96 0 24.48 0 14.88 0 6.62 4.02 2.67 12.82l8.31 6.24c1.9-5.72 7.22-9.96 13.5-9.96z" fill="#EA4335" />
                      </g>
                      <defs>
                        <clipPath id="clip0_17_40">
                          <rect width="48" height="48" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <span className="ml-2">Link Google Account</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <form className="space-y-5 mt-8">
              <div className="flex flex-col items-center mb-6">
                <div className="relative mb-4 w-24 h-24 rounded-full overflow-hidden">

                  <LoadingMask loading={isUploading} /> {/* Add LoadingMask */}
                  {/* Use stable key and source URL to prevent flickering */}
                  <Avatar
                    key={`profile-avatar-static`}
                    src={profileImage || profileImageUrl || ''}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    className="absolute inset-0 flex items-center justify-center rounded-full p-2 text-white hover:bg-black/70"
                    onClick={() => document.getElementById('profile-image').click()}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </button>
                  <input
                    id="profile-image"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                </div>

                {isEditingName ? (
                  <div className="flex items-center mb-2">
                    <input
                      type="text"
                      value={newUserName}
                      onChange={(e) => setNewUserName(e.target.value)}
                      className="text-lg font-medium text-gray-900 border border-gray-300 rounded-md px-2 py-1"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleUpdateUserName();
                        } else if (e.key === 'Escape') {
                          setIsEditingName(false);
                          setNewUserName(userName || userEmail?.split('@')[0] || 'User');
                        }
                      }}
                      onBlur={() => {
                        if (newUserName !== userName && newUserName.trim() !== '') {
                          handleUpdateUserName();
                        } else {
                          setIsEditingName(false);
                          setNewUserName(userName || userEmail?.split('@')[0] || 'User');
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex items-center mb-2">
                    <div
                      className="text-lg font-medium text-gray-900 mr-2 hover:bg-gray-100 px-2 py-1 cursor-pointer rounded"
                      onClick={() => setIsEditingName(true)}
                      title="Click to edit username"
                    >
                      {newUserName}
                    </div>
                  </div>
                )}
                <div className="text-sm text-gray-500">{userEmail}</div>
                <div className="text-sm text-gray-500">Account Number : {acctNo}</div>
                <div className="text-sm text-gray-500">Account Id : {acctId}</div>
              </div>
            </form>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <form onSubmit={handleChangePassword} className="space-y-5 mt-8">
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <div className="mt-1">
                  <input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-black focus:outline-none focus:ring-black sm:text-sm"
                    required
                  />
                </div>
              </div>
              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                  Confirm New Password
                </label>
                <div className="mt-1">
                  <input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-black focus:outline-none focus:ring-black sm:text-sm"
                    required
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex justify-center rounded-md border border-transparent bg-black py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-75"
                >
                  {isSubmitting ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          )}

          {/* Email Tab */}
          {activeTab === 'email' && (
            <form onSubmit={handleUpdateEmail} className="space-y-5 mt-8">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-black focus:outline-none focus:ring-black sm:text-sm"
                    required
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex justify-center rounded-md border border-transparent bg-black py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-75"
                >
                  {isSubmitting ? 'Updating...' : 'Update Email'}
                </button>
              </div>
            </form>
          )}

          {/* Phone Number Tab */}
          {activeTab === 'phone' && (
            <form onSubmit={handleUpdatePhoneNumber} className="space-y-5 mt-8">
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <div className="mt-1">
                  <input
                    id="phoneNumber"
                    type="tel"
                    value={newPhoneNumber}
                    onChange={(e) => setNewPhoneNumber(e.target.value)}
                    placeholder="+1 (234) 567-8901"
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-black focus:outline-none focus:ring-black sm:text-sm"
                    required
                  />
                </div>
                {/* Phone number validation message */}
                {newPhoneNumber && !/^\D*([0-9]{7,15})\D*$/.test(newPhoneNumber.replace(/\D/g, '')) && (
                  <div className="text-red-600 text-xs mt-2">Phone number must be between 7 and 15 digits.</div>
                )}
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !newPhoneNumber ||
                    !/^\D*([0-9]{7,15})\D*$/.test(newPhoneNumber.replace(/\D/g, '')) ||
                    newPhoneNumber === phone
                  }
                  className="inline-flex justify-center rounded-md border border-transparent bg-black py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-75"
                >
                  {isSubmitting ? 'Updating...' : 'Update Phone Number'}
                </button>
              </div>
            </form>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

export default ProfileDialog;
